import { registerDecorator, ValidatorConstraint, ValidatorConstraintInterface } from 'class-validator'

/**
 * Validates a French SIRET number by checking:
 * - Total length is exactly 14 digits
 * - First 9 digits (SIREN) are valid using Luhn algorithm
 * - Ignores the last 5 digits (NIC) validation
 */
export function isValidSiret(siret: string): boolean {
  // Remove any spaces or non-digit characters
  const cleanSiret: string = siret.replace(/[^0-9]/g, '')

  // Check if it's exactly 14 digits
  if (cleanSiret.length !== 14) {
    return false
  }

  // Extract SIREN (first 9 digits)
  const siren: string = cleanSiret.substring(0, 9)

  // Validate SIREN using Luhn algorithm
  let sum: number = 0
  let alternate: boolean = false

  // Process SIREN digits from right to left
  for (let i: number = siren.length - 1; i >= 0; i--) {
    let digit: number = parseInt(siren.charAt(i), 10)

    if (alternate) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    alternate = !alternate
  }

  return sum % 10 === 0
}

@ValidatorConstraint({ name: 'isValidSiret', async: false })
export class IsValidSiretConstraint implements ValidatorConstraintInterface {
  validate(siret: string): boolean {
    return isValidSiret(siret)
  }

  defaultMessage(): string {
    return 'SIRET number is not valid'
  }
}

export function IsValidSiret() {
  return function (object: object, propertyName: string): void {
    registerDecorator({
      target: object.constructor,
      propertyName: propertyName,
      validator: IsValidSiretConstraint,
    })
  }
}
